import type { CaptureType, CurrentFocus } from "@idea-studio/shared";

interface Props {
  onPick: (type: CaptureType) => void;
  onRun: () => void;
  onInsights: () => void;
  focus?: CurrentFocus;
  mode?: string;
}

interface Row {
  id: string;
  label: string;
  hint: string;
  onClick: () => void;
}

export function HomeScreen({ onPick, onRun, onInsights, focus, mode }: Props) {
  const capture: Row[] = [
    { id: "problem", label: "Probleem", hint: "Vastloper of frustratie", onClick: () => onPick("problem") },
    { id: "idea", label: "Idee", hint: "Business-case kandidaat", onClick: () => onPick("idea") },
    { id: "improvement", label: "Verbetering", hint: "Studio of app verfijnen", onClick: () => onPick("improvement") },
  ];
  const system: Row[] = [
    { id: "run", label: "Run systeem", hint: "Volgende stap uitvoeren", onClick: onRun },
    { id: "insights", label: "Inzichten", hint: "Status, blokkade, shortlist", onClick: onInsights },
  ];

  return (
    <div className="space-y-9">
      <Group label="Capture" rows={capture} />
      <Group label="Systeem" rows={system} />
      {focus && <FocusStrip focus={focus} mode={mode} />}
    </div>
  );
}

function Group({ label, rows }: { label: string; rows: Row[] }) {
  return (
    <section>
      <h2 className="mb-3 px-1 text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
        {label}
      </h2>
      <div className="overflow-hidden rounded-2xl border border-neutral-800/80 bg-neutral-900/40">
        {rows.map((r, idx) => (
          <button
            key={r.id}
            type="button"
            onClick={r.onClick}
            className={
              "flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors active:bg-neutral-800/60 " +
              (idx > 0 ? "border-t border-neutral-800/70" : "")
            }
          >
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="text-[15px] font-medium text-neutral-100">{r.label}</span>
              <span className="text-[13px] text-neutral-500">{r.hint}</span>
            </span>
            <Chevron />
          </button>
        ))}
      </div>
    </section>
  );
}

function FocusStrip({ focus, mode }: { focus: CurrentFocus; mode?: string }) {
  return (
    <section className="border-t border-neutral-900 pt-5">
      <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
        <span>Cyclus {String(focus.active_cycle ?? "—")}</span>
        <span className="text-neutral-700">·</span>
        <span>{mode ?? focus.discovery_mode ?? "—"}</span>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-neutral-400">
        {focus.next_action ?? focus.blocker ?? "—"}
      </p>
    </section>
  );
}

function Chevron() {
  return (
    <svg
      className="h-4 w-4 shrink-0 text-neutral-600"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
